import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import JsxParser from 'react-jsx-parser';
import { Link, useNavigate } from 'react-router-dom';

import { getPromptPillValue } from '@helpers';
import { Button, LoaderSkeleton, Pill } from '@utilities';

import styles from './_index.module.scss';
import { readGlobalFilters, readSurveyResponseCounts } from '@api/survey_dashboard';
import { readExternalQuestions } from '@api/instant_survey';
import { resolvePromptGroups } from '@api/narratives/utilities/helpers';

const ItemAnswers = ({ item, readItemAnswers, readPrompts, type }) => {
  const [answers, setAnswers] = useState(null);
  const [detailsString, setDetailsString] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [offsetHeight, setOffsetHeight] = useState(0);
  const [prompts, setPrompts] = useState(null);
  const [surveyQuestions, setSurveyQuestions] = useState(null);
  const [surveyGlobalFilters, setSurveyGlobalFilters] = useState(null);
  const [totalResponses, setTotalResponses] = useState(null);

  const navigate = useNavigate();
  const ref = useRef(null);
  const thresholdHeight = 225;

  useEffect(() => {
    if (ref.current) {
      setOffsetHeight(ref.current.offsetHeight);
    }
  }, [answers, prompts]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const getItemAnswers = async () => {
      try {
        const response = await readItemAnswers({ id: item?.id, item, signal });
        setAnswers(response?.data?.answers || response?.answers);
        setDetailsString(response?.data?.detailsString);
      } catch (error) {
        console.error(error);
      }
    };

    const getPrompts = async () => {
      try {
        const response = await readPrompts({
          ...item,
          flowId: item?.id,
          signal,
        });
        setPrompts(response?.data || response);
      } catch (error) {
        console.error(error);
      }
    };

    getPrompts();
    getItemAnswers();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!item.isSurvey || !answers) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const getSurveyGlobalFilters = async () => {
      try {
        setSurveyGlobalFilters(
          await readGlobalFilters({
            signal,
            surveyId: answers.survey_id,
          })
        );
      } catch (error) {
        console.error(error);
      }
    };

    const getSurveyQuestions = async () => {
      try {
        setSurveyQuestions(
          await readExternalQuestions({
            signal,
            surveySourceId: answers.survey_source_id,
          })
        );
      } catch (error) {
        console.error(error);
      }
    };

    const getTotalSurveyResponseCounts = async () => {
      try {
        const surveyResponseCounts = await readSurveyResponseCounts({
          signal,
          surveyId: answers.survey_id,
        });
        setTotalResponses(surveyResponseCounts.totalResponses || 0);
      } catch (error) {
        console.error(error);
      }
    };

    getSurveyQuestions();
    getSurveyGlobalFilters();
    getTotalSurveyResponseCounts();

    return () => controller.abort();
  }, [answers]);

  const demographicFiltersWithLabels = useMemo(() => {
    if (!item?.isSurvey || !answers || !surveyGlobalFilters) return [];

    const demographicFilter = answers?.demographic_filter || {};
    return Object.keys(demographicFilter).map((key) => {
      const globalFilter = surveyGlobalFilters?.demographicFilters.find(({ id }) => id === key);
      return { id: key, label: globalFilter?.label || key, values: demographicFilter[key] };
    });
  }, [item, answers, surveyGlobalFilters]);

  const optionFilterQuestionList = useMemo(() => {
    if (!item?.isSurvey || !answers || !surveyQuestions) return [];

    const optionFilterLabels = surveyQuestions?.reduce((acc, question) => {
      const { options, title: questionTitle } = question;
      if (!options || options.length === 0) return acc;
      options.forEach((option) => {
        const { id: optionId, title: optionTitle } = option;
        acc[optionId] = {
          optionId,
          optionTitle,
          questionId: question.id,
          questionTitle,
        };
      });
      return acc;
    }, {});

    const optionFilter = answers?.option_filter || [];
    const optionFilterQuestionMap = optionFilter.reduce((acc, optionId) => {
      const option = optionFilterLabels[optionId];
      if (!option) return acc;
      const { optionTitle, questionId, questionTitle } = option;
      if (!acc[questionId]) {
        acc[questionId] = {
          options: [],
          questionTitle,
        };
      }
      acc[questionId].options.push({
        optionId,
        optionTitle,
      });
      return acc;
    }, {});

    return Object.entries(optionFilterQuestionMap).map(
      ([questionId, { options, questionTitle }]) => ({
        options,
        questionId,
        questionTitle,
      })
    );
  }, [item?.isSurvey, answers?.option_filter, surveyQuestions]);

  const quotaGroupFilter = useMemo(() => {
    if (!item?.isSurvey || !answers || !surveyGlobalFilters) return [];

    const quotaGroups = answers?.quota_group_filter || [];
    return quotaGroups
      .map((groupRef) =>
        surveyGlobalFilters?.quotaGroups.find(({ quotaGroupRefs }) =>
          quotaGroupRefs.includes(groupRef)
        )
      )
      .filter(Boolean)
      .reduce((acc, group) => {
        if (
          acc.findIndex(
            ({ instantSurveyGroupRef }) => instantSurveyGroupRef === group.instantSurveyGroupRef
          ) === -1
        ) {
          acc.push(group);
        }
        return acc;
      }, []);
  }, [item?.isSurvey, answers?.quota_group_filter, surveyGlobalFilters?.quotaGroups]);

  const answerDataNotLoaded = !answers || !prompts;
  const surveyDataNotLoaded =
    item?.isSurvey && (!surveyGlobalFilters || !surveyQuestions || !totalResponses);

  if (answerDataNotLoaded || surveyDataNotLoaded) {
    return (
      <LoaderSkeleton height={20} width={2000}>
        <rect height="20" rx="2" ry="2" width="100%" x="0" y="0" />
      </LoaderSkeleton>
    );
  }

  if (item?.isSurvey) {
    return (
      <>
        <div
          className={classNames(styles['item-answers'], {
            [styles['is-expandable']]: offsetHeight >= thresholdHeight,
            [styles['is-expanded']]: isExpanded,
          })}
          ref={ref}
        >
          <small className={styles['item-answers-details']}>
            <JsxParser
              components={{
                a: ({ children, href }) => <Link to={href}>{children}</Link>,
              }}
              jsx={detailsString}
              renderInWrapper={false}
            />
          </small>

          <div className={styles['item-answers-section']}>
            <h4>Sample Size</h4>
            <p>{`${answers?.sample_size} / ${totalResponses} available respondents`}</p>
          </div>

          {demographicFiltersWithLabels.length > 0 && (
            <div className={styles['item-answers-section']}>
              <h4>Demographic Filters</h4>
              <ul className={styles['item-answers-section-pill-list']}>
                {demographicFiltersWithLabels.map(({ id, label, values }) =>
                  values.map((value, index) => (
                    <li key={`demographic-filter-${id}-${index}`}>
                      <Pill isCompleted value={`${label}: ${value}`} />
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}

          {optionFilterQuestionList.length > 0 && (
            <div className={styles['item-answers-section']}>
              <h4>Question Filters</h4>
              {optionFilterQuestionList.map(({ options, questionId, questionTitle }) => (
                <div
                  className={styles['item-answers-section-question-filter']}
                  key={`question-filter-${questionId}`}
                >
                  <strong>{questionTitle}</strong>
                  <ul className={styles['item-answers-section-question-list']}>
                    {options.map(({ optionId, optionTitle }) => (
                      <li key={`option-filter-${optionId}`}>{optionTitle}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {quotaGroupFilter.length > 0 && (
            <div className={styles['item-answers-section']}>
              <h4>Quota Group Filters</h4>
              <ul className={styles['item-answers-section-pill-list']}>
                {quotaGroupFilter.map(({ instantSurveyGroupRef, name }) => (
                  <li key={`quota-group-filter-${instantSurveyGroupRef}`}>
                    <Pill isCompleted value={name} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </>
    );
  }

  if (item?.isFrozen || item?.isSmart || item?.isJoin)
    return (
      <small>
        <JsxParser
          components={{
            a: ({ children, href }) => <Link to={href}>{children}</Link>,
          }}
          jsx={detailsString}
          renderInWrapper={false}
        />
      </small>
    );

  if (!answers || isEmpty(answers)) {
    if (type === 'People Group') {
      return <small>This people group includes all shoppers.</small>;
    }
    if (type === 'Trip Group') {
      return <small>This is a regular trip group.</small>;
    }
  }

  const promptGroups = Array.isArray(prompts?.promptGrouping)
    ? prompts.promptGrouping
    : prompts?.promptGrouping
      ? [prompts.promptGrouping]
      : [];
  const answerKeys = Object.keys(answers);
  const promptMetadata = answerKeys
    .filter((key) => answers[key] != null && prompts?.[key])
    .map((key) => ({ ...prompts[key], promptKey: key }));
  const groupedPromptKeys = new Set();
  const groupedAnswers = resolvePromptGroups(promptMetadata, promptGroups).map(
    ({ group, groupIndex, promptKeys }) => {
      promptKeys.forEach((key) => groupedPromptKeys.add(key));
      return { group, key: `group-${groupIndex}`, promptKeys };
    }
  );
  const answerSections = [
    ...answerKeys
      .filter((key) => !groupedPromptKeys.has(key))
      .map((key) => ({ key, promptKeys: [key] })),
    ...groupedAnswers,
  ];

  return (
    <>
      <div
        className={classNames(styles['item-answers'], {
          [styles['is-expandable']]: offsetHeight >= thresholdHeight,
          [styles['is-expanded']]: isExpanded,
        })}
        ref={ref}
        style={{ maxHeight: !isExpanded ? thresholdHeight : 'none' }}
      >
        {answerSections.map(({ group, key, promptKeys }) => {
          const validPromptKeys = promptKeys.filter((promptKey) => prompts?.[promptKey]);
          if (!validPromptKeys.length) return null;
          const promptsWithAnswers = validPromptKeys.map((promptKey) => prompts[promptKey]);

          return (
            <div key={key}>
              <h4>{group?.name || promptsWithAnswers[0]?.name}</h4>
              <ul>
                {validPromptKeys.flatMap((promptKey) => {
                  const prompt = prompts[promptKey];
                  const values =
                    promptKey === 'date_range' || promptKey === 'date_range_only_custom'
                      ? [answers[promptKey]]
                      : answers[promptKey];

                  return values.map((value, valueIndex) => (
                    <li key={`${promptKey}-${valueIndex}`}>
                      <Pill
                        icon={value?.icon}
                        isCompleted={!value?.href}
                        onClick={value?.href ? () => navigate(value.href) : () => {}}
                        value={getPromptPillValue({
                          answers: { [prompt.id]: Array.isArray(value) ? value : [value] },
                          prompt,
                        })}
                      />
                    </li>
                  ));
                })}
              </ul>
            </div>
          );
        })}
      </div>
      {offsetHeight >= thresholdHeight && (
        <div className={styles['item-answers-expanded']}>
          <Button
            data-testid="item-answers-show-more"
            onClick={() => setIsExpanded((isExpanded) => !isExpanded)}
            text={isExpanded ? 'Collapse' : 'Expand'}
            variant="secondary"
          />
        </div>
      )}
    </>
  );
};

ItemAnswers.propTypes = {
  item: PropTypes.object.isRequired,
  readItemAnswers: PropTypes.func.isRequired,
  readPrompts: PropTypes.func.isRequired,
  type: PropTypes.string,
};

export default ItemAnswers;
